import type {
  Operator,
  QueueInfo,
  ServerMessage,
  ServiceStatus,
} from '../state/types';
import {
  ChatApiError,
  type ChatApiService,
  type ConversationState,
  type StartAnonymousInput,
  type StartAuthenticatedInput,
} from './service';

export type StubConfig = {
  startDelayMs?: number;
  pollDelayMs?: number;
  sendDelayMs?: number;
  simulateCaptchaError?: number;
  simulateSendError?: number;
  serviceStatus?: ServiceStatus;
  /** Tijd in ms dat de visitor in queue blijft voordat de operator "attached" */
  queueDelayMs?: number;
};

const DEFAULT_SERVICE_STATUS: ServiceStatus = {
  isAcceptingConversationRequests: true,
  showChatClientWhenOffline: true,
  maxMessageLength: 4000,
  turnstile: null,
  openingHours: [
    { day: 'monday',    label: 'Maandag',   hours: '08:30 – 17:00' },
    { day: 'tuesday',   label: 'Dinsdag',   hours: '08:30 – 17:00' },
    { day: 'wednesday', label: 'Woensdag',  hours: '08:30 – 20:00' },
    { day: 'thursday',  label: 'Donderdag', hours: '08:30 – 17:00' },
    { day: 'friday',    label: 'Vrijdag',   hours: '08:30 – 16:00' },
    { day: 'saturday',  label: 'Zaterdag',  hours: 'Gesloten', closed: true },
    { day: 'sunday',    label: 'Zondag',    hours: 'Gesloten', closed: true },
  ],
  todayIndex: ((new Date().getDay() + 6) % 7), // JS: 0=sun, wij: 0=mon
};

const OPERATOR: Operator = {
  name: 'Sanne Jansen',
  role: 'Woonconsulent',
  initials: 'SJ',
  avatarColor: '',     // leeg → rendering valt terug op `var(--ec-accent)`
};

const OPERATOR_REPLIES = [
  'Bedankt voor je bericht! Ik kijk er even naar.',
  'Duidelijk, ik zoek het voor je uit.',
  'Een moment, ik check dat voor je.',
  'Dank je wel voor de info — ik ben er zo.',
];

type SessionData = {
  id: string;
  visitor: { firstName: string; email: string };
  initialQuestion: string;
  messages: ServerMessage[];
  isEnded: boolean;
  operator: Operator | null;
  agentTyping: boolean;
  queueInfo: QueueInfo | null;
  agentAttachesAt: number; // timestamp (ms) waarop operator online komt
};

export class StubChatApiService implements ChatApiService {
  private readonly startDelayMs: number;
  private readonly pollDelayMs: number;
  private readonly sendDelayMs: number;
  private readonly simulateCaptchaError: number;
  private readonly simulateSendError: number;
  private readonly serviceStatus: ServiceStatus;
  private readonly queueDelayMs: number;

  private readonly sessions = new Map<string, SessionData>();
  private sessionCounter = 0;
  private messageCounter = 0;

  constructor(config: StubConfig = {}) {
    this.startDelayMs = config.startDelayMs ?? 300;
    this.pollDelayMs = config.pollDelayMs ?? 50;
    this.sendDelayMs = config.sendDelayMs ?? 150;
    this.simulateCaptchaError = config.simulateCaptchaError ?? 0;
    this.simulateSendError = config.simulateSendError ?? 0;
    this.serviceStatus = config.serviceStatus ?? DEFAULT_SERVICE_STATUS;
    this.queueDelayMs = config.queueDelayMs ?? 3000;
  }

  async fetchServiceStatus(): Promise<ServiceStatus> {
    await this.delay(50);
    return { ...this.serviceStatus };
  }

  async startAnonymous(input: StartAnonymousInput): Promise<string> {
    await this.delay(this.startDelayMs);

    if (Math.random() < this.simulateCaptchaError) {
      throw new ChatApiError('captcha_invalid', 'Captcha verification failed (simulated).');
    }

    const sessionId = this.nextSessionId();
    const now = Date.now();
    const initialMsg: ServerMessage = {
      id: this.nextMessageId(),
      text: input.initialQuestion,
      author: 'customer',
      timestamp: now,
    };

    this.sessions.set(sessionId, {
      id: sessionId,
      visitor: { firstName: input.firstName, email: input.emailAddress },
      initialQuestion: input.initialQuestion,
      messages: input.initialQuestion ? [initialMsg] : [],
      isEnded: false,
      operator: null,                              // begin in queue
      agentTyping: false,
      queueInfo: { position: 2, estimatedWaitMin: 3 },
      agentAttachesAt: now + this.queueDelayMs,
    });

    return sessionId;
  }

  async startAuthenticated(input: StartAuthenticatedInput): Promise<string> {
    // Stub-mode kent geen echte JWT-validatie — we hergebruiken de
    // anonymous-flow met een placeholder-visitor zodat demo's en tests
    // niet hoeven te zien wie er ingelogd is.
    return this.startAnonymous({
      firstName: 'Ingelogd',
      lastName: '-',
      emailAddress: 'ingelogd@stub.local',
      initialQuestion: input.initialQuestion,
      captchaToken: '',
    });
  }

  async getState(sessionId: string): Promise<ConversationState> {
    await this.delay(this.pollDelayMs);

    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new ChatApiError('session_expired', `Session ${sessionId} not found.`);
    }

    // Check queue-timer: als tijd verstreken is → attach operator + welkomstbericht
    if (session.operator === null && Date.now() >= session.agentAttachesAt) {
      session.operator = OPERATOR;
      session.queueInfo = null;
      const firstName = session.visitor.firstName || 'daar';
      session.messages.push({
        id: this.nextMessageId(),
        text: `Hallo ${firstName}, ik ben ${OPERATOR.name}. Ik lees je vraag even door en kom zo bij je terug.`,
        author: 'operator',
        timestamp: Date.now(),
      });
    } else if (session.operator === null && session.queueInfo) {
      // Update queue-info naarmate tijd verstrijkt
      const remaining = Math.max(0, session.agentAttachesAt - Date.now());
      session.queueInfo = {
        position: remaining > 2000 ? 2 : 1,
        estimatedWaitMin: Math.ceil(remaining / 60000) || 1,
      };
    }

    return {
      messages: session.messages.map((m) => ({ ...m })),
      isEnded: session.isEnded,
      operator: session.operator ? { ...session.operator } : null,
      agentTyping: session.agentTyping,
      queueInfo: session.queueInfo,
    };
  }

  async sendMessage(sessionId: string, text: string): Promise<void> {
    await this.delay(this.sendDelayMs);

    if (Math.random() < this.simulateSendError) {
      throw new ChatApiError('network', 'Simulated network error.');
    }

    const session = this.sessions.get(sessionId);
    if (!session) {
      throw new ChatApiError('session_expired', `Session ${sessionId} not found.`);
    }

    session.messages.push({
      id: this.nextMessageId(),
      text,
      author: 'customer',
      timestamp: Date.now(),
    });

    // Alleen typing + reply sturen als operator online is
    if (session.operator) {
      // Typing indicator gaat aan voor ~1s, dan operator-reply
      session.agentTyping = true;
      setTimeout(() => {
        session.agentTyping = false;
        if (this.sessions.has(sessionId) && !session.isEnded) {
          session.messages.push({
            id: this.nextMessageId(),
            text: this.pickReply(),
            author: 'operator',
            timestamp: Date.now(),
          });
        }
      }, 1200 + Math.random() * 800);
    }
  }

  async endConversation(sessionId: string): Promise<void> {
    await this.delay(this.sendDelayMs);
    const session = this.sessions.get(sessionId);
    if (!session) return; // best-effort, geen 404 gooien
    session.isEnded = true;
    session.agentTyping = false;
  }

  async rateConversation(sessionId: string, _rating: number): Promise<void> {
    await this.delay(this.sendDelayMs);
    // Stub-mirror van de server-call. Het echte server-side resultaat
    // (rating opslaan op de conversatie) modelleren we niet — onze poll-
    // response heeft geen rating-veld. Best-effort: missing session-id
    // veroorzaakt geen throw (zelfde semantiek als endConversation).
    void sessionId;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private nextSessionId(): string {
    this.sessionCounter += 1;
    return `stub-${this.sessionCounter.toString().padStart(8, '0')}-${Date.now().toString(16)}`;
  }

  private nextMessageId(): string {
    this.messageCounter += 1;
    return `stub-msg-${this.messageCounter}`;
  }

  private pickReply(): string {
    const index = Math.floor(Math.random() * OPERATOR_REPLIES.length);
    return OPERATOR_REPLIES[index] ?? OPERATOR_REPLIES[0]!;
  }
}
